import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { COLLECTIONS, COLLECTION_KEYS } from "@/data/products";
import { waColor } from "@/lib/whatsapp";

const labelFor = {
    "exterior-texture": "Exterior · Textured",
    "exterior-premium": "Exterior · Premium",
    "interior-premium": "Interior · Premium",
    "easy-care": "Easy Care",
};

// Choose readable text color against a hex background
const readableInk = (hex) => {
    const h = hex.replace("#", "");
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.6 ? "#0E0E10" : "#F4EFE6";
};

export const ColorGrid = ({ compact = false }) => {
    const [active, setActive] = useState("interior-premium");
    const collection = COLLECTIONS[active];

    const visible = compact ? collection.colors.slice(0, 12) : collection.colors;

    return (
        <section id="catalogue" className="relative py-24 md:py-32 bg-bone" data-testid="color-grid-section">
            <div className="mx-auto max-w-[1440px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-12 md:mb-16">
                    <div className="col-span-12 md:col-span-4">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-ink/50">
                            Chapter · 06 / Colour Library
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.02em]">
                            {collection.title.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="italic text-brand-red">
                                {collection.title.split(" ").slice(-1)}
                            </span>
                        </h2>
                        <p className="mt-4 text-ink/70 max-w-lg">{collection.subtitle}</p>
                    </div>
                </div>

                {/* Category tabs */}
                <div className="flex flex-wrap gap-2 mb-10 md:mb-14">
                    {COLLECTION_KEYS.map((k) => (
                        <button
                            key={k}
                            onClick={() => setActive(k)}
                            className={`btn-pill border ${
                                active === k
                                    ? "bg-ink text-bone border-ink"
                                    : "border-ink/20 hover:border-ink/60"
                            }`}
                            data-testid={`collection-tab-${k}`}
                        >
                            {labelFor[k]}
                        </button>
                    ))}
                </div>

                {/* Color grid */}
                <motion.div
                    key={active}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4"
                    data-testid="color-grid"
                >
                    {visible.map((c, i) => {
                        const ink = readableInk(c.hex);
                        return (
                            <motion.a
                                key={c.code}
                                href={waColor(c, collection.title)}
                                target="_blank"
                                rel="noreferrer"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                                className="group relative block aspect-[4/5] overflow-hidden ring-1 ring-ink/10"
                                style={{ background: c.hex, color: ink }}
                                data-testid={`color-${c.code.replace(/\s+/g, "-")}`}
                            >
                                <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-between">
                                    <div className="flex items-start justify-between">
                                        <span className="font-mono text-[10px] tracking-widest opacity-70">
                                            {c.code}
                                        </span>
                                        <ArrowUpRight
                                            size={16}
                                            className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                                        />
                                    </div>
                                    <div>
                                        <div className="font-display text-lg md:text-xl leading-tight">
                                            {c.name}
                                        </div>
                                        <div className="mt-1 text-[10px] font-mono uppercase tracking-widest opacity-60">
                                            Tap · WhatsApp
                                        </div>
                                    </div>
                                </div>
                            </motion.a>
                        );
                    })}
                </motion.div>

                {compact && (
                    <div className="mt-10 flex justify-center">
                        <a
                            href="/catalogue"
                            className="btn-pill bg-ink text-bone hover:bg-brand-red"
                            data-testid="see-full-catalogue"
                        >
                            See the full colour library <ArrowUpRight size={16} />
                        </a>
                    </div>
                )}

                <p className="mt-10 text-xs text-ink/50 max-w-2xl">
                    Displayed shades are approximate and may differ from the actual paint. Exclusive
                    off-catalogue colours available on request.
                </p>
            </div>
        </section>
    );
};
